import clsx from "clsx";
import React from "react";
import { t } from "ttag";
import { Button, Checkbox, DeleteIcon, Select, TextField, Tooltip, Typography } from "@coolremote/shared";
import { IEnumEntry, ParamType, Verify } from "../types";
import { isEnumType, Option, PARAM_TYPE_OPTIONS, RW_BOOL_OPTIONS } from "./options";
import styles from "./RegisterTable.module.css";

export interface IRegisterColumn<T> {
  /** Row field this column edits. Required for the built-in `kind` editors. */
  key?: keyof T & string;
  label?: React.ReactNode;
  /** Explains the column on hover — shown as a tooltip on the header label. Plain string, or JSX for partial formatting (e.g. bolding an example). */
  tooltip?: React.ReactNode;
  /** Passed straight to the column header `<th style={{ width }}>`. */
  width?: number | string;
  /** Built-in editor. Omit and provide `render` for anything bespoke. */
  kind?: "text" | "number" | "select" | "rwBool";
  options?: Option[];
  /** `kind: "select"` — cap the dropdown height so long lists scroll. */
  maxMenuHeight?: number | string;
  /** `kind: "select"` — widen the dropdown beyond its (often narrow) column. */
  minMenuWidth?: number | string;
  placeholder?: string;
  /** For `kind: "number"` — clamp the entered value to this range. */
  min?: number;
  max?: number;
  /** For `kind: "number"` — only allow whole numbers. */
  integer?: boolean;
  align?: "left" | "center" | "right";
  /** Disable just this column's built-in editor (independent of the table-level `readOnly`). */
  disabled?: boolean;
  /** Per-row disabled logic — called with the current row; overrides `disabled` when true. */
  disabledFn?: (row: T) => boolean;
  /** Freeze this column to the left while the table scrolls horizontally (use with `scrollX`). */
  sticky?: boolean;
  /** Escape hatch for non-trivial cells (verify cluster, param type + enum map, …). */
  render?: (ctx: { row: T; index: number; update: (mutator: (row: T) => T) => void }) => React.ReactNode;
}

export interface IRegisterTableProps<T> {
  columns: IRegisterColumn<T>[];
  rows: T[];
  onUpdateRow: (index: number, mutator: (row: T) => T) => void;
  onRemoveRow?: (index: number) => void;
  onAddRow?: () => void;
  addLabel?: string;
  removeAriaLabel?: string;
  className?: string;
  /** Render a leading 1-based row-number column. */
  showIndex?: boolean;
  indexLabel?: React.ReactNode;
  /** Per-cell validation error, surfaced on the built-in text/number/select editors. */
  getCellError?: (row: T, index: number, key: string) => string | undefined;
  /** Override the default red "×" remove control (e.g. with a trash icon). */
  renderRemove?: (index: number) => React.ReactNode;
  /** Wrap the table in a horizontal scroll area (use with fixed px column widths). */
  scrollX?: boolean;
  /** Ref to the horizontal scroll container (only when `scrollX`), for imperative scrolling. */
  scrollRef?: React.Ref<HTMLDivElement>;
  /** Render every built-in editor disabled and hide add/remove (display-only). */
  readOnly?: boolean;
}

function renderBuiltIn<T>(
  column: IRegisterColumn<T>,
  row: T,
  set: (value: any) => void,
  error?: string,
  readOnly?: boolean,
) {
  const isDisabled = readOnly || column.disabled || (column.disabledFn ? column.disabledFn(row) : false);
  const value = (row as any)[column.key as string];
  if (column.kind === "number") {
    const bounded = column.min != null || column.max != null;
    return (
      <TextField
        size="small"
        type="number"
        fullWidth
        inputClassName={styles.noSpinner}
        disabled={isDisabled}
        error={error}
        placeholder={column.placeholder}
        min={column.min}
        max={column.max}
        step={column.integer ? 1 : undefined}
        value={String(value ?? "")}
        onChange={(v: string) => {
          // Empty clears bounded fields to null (unset); unbounded keep the 0 default.
          if (v === "") {
            set(bounded ? null : 0);
            return;
          }
          let n = Number(v);
          if (Number.isNaN(n)) return;
          if (column.integer) n = Math.trunc(n);
          if (column.min != null) n = Math.max(column.min, n);
          if (column.max != null) n = Math.min(column.max, n);
          set(n);
        }}
      />
    );
  }
  if (column.kind === "select") {
    return (
      <Select
        fullWidth
        menuPortal
        disabled={isDisabled}
        error={error}
        options={column.options || []}
        value={value}
        onChange={(v: string | number) => set(v)}
        maxMenuHeight={column.maxMenuHeight}
        minMenuWidth={column.minMenuWidth}
        classes={{ trigger: styles.selectTrigger }}
      />
    );
  }
  if (column.kind === "rwBool") {
    return (
      <Select
        fullWidth
        menuPortal
        disabled={isDisabled}
        error={error}
        options={RW_BOOL_OPTIONS}
        value={value ? "R" : "R/W"}
        onChange={(v: string | number) => set(v === "R")}
        classes={{ trigger: styles.selectTrigger }}
      />
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      disabled={isDisabled}
      error={error}
      placeholder={column.placeholder}
      value={(value as string) ?? ""}
      onChange={(v: string) => set(v)}
    />
  );
}

export function RegisterTable<T>({
  columns,
  rows,
  onUpdateRow,
  onRemoveRow,
  onAddRow,
  addLabel,
  removeAriaLabel,
  className,
  showIndex,
  indexLabel,
  getCellError,
  renderRemove,
  scrollX,
  scrollRef,
  readOnly,
}: IRegisterTableProps<T>) {
  const showRemove = !readOnly && !!(onRemoveRow || renderRemove);

  const table = (
    <table className={clsx(styles.table, scrollX && styles.tableScrollContent)}>
      <thead>
        <tr>
          {showIndex && <th className={styles.idHead}>{indexLabel ?? t`ID`}</th>}
          {columns.map((column, colIndex) => (
            <th
              key={column.key ?? colIndex}
              className={clsx(column.sticky && styles.stickyCol)}
              style={{ width: column.width, textAlign: column.align }}
            >
              {column.tooltip ? (
                <Tooltip title={column.tooltip} placement="top">
                  <span className={styles.headerLabel}>{column.label}</span>
                </Tooltip>
              ) : (
                column.label
              )}
            </th>
          ))}
          {showRemove && <th className={styles.thRemove} />}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const update = (mutator: (r: T) => T) => onUpdateRow(index, mutator);
          return (
            <tr key={index}>
              {showIndex && <td className={styles.idCell}>{index + 1}</td>}
              {columns.map((column, colIndex) => (
                <td
                  key={column.key ?? colIndex}
                  className={clsx(column.sticky && styles.stickyCol)}
                  style={{ textAlign: column.align }}
                >
                  {column.render
                    ? column.render({ row, index, update })
                    : renderBuiltIn(
                        column,
                        row,
                        (value) => update((r) => ({ ...r, [column.key as string]: value })),
                        column.key ? getCellError?.(row, index, column.key) : undefined,
                        readOnly,
                      )}
                </td>
              ))}
              {showRemove && (
                <td>
                  {renderRemove ? (
                    renderRemove(index)
                  ) : (
                    <Button
                      white
                      size="small"
                      classes={{ root: styles.removeBtnSize }}
                      onClick={() => onRemoveRow!(index)}
                      aria-label={removeAriaLabel || t`Remove row`}
                    >
                      <DeleteIcon width={14} height={14} />
                    </Button>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className={clsx(styles.tableShell, className)}>
      {scrollX ? <div ref={scrollRef} className={styles.scrollX}>{table}</div> : table}
      {onAddRow && !readOnly && (
        <div className={styles.addRow}>
          <Button white size="small" onClick={onAddRow}>
            + {addLabel || t`Add Line`}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Reusable special cells — drop these into a column's `render`.
 * ------------------------------------------------------------------ */

interface IParamTypeCellProps {
  value: ParamType;
  enumMap?: IEnumEntry[];
  onChange: (value: ParamType) => void;
  onOpenMap?: () => void;
}

export const ParamTypeCell: React.FC<IParamTypeCellProps> = ({ value, enumMap, onChange, onOpenMap }) => {
  return (
    <div className={styles.paramTypeCell}>
      <Select
        fullWidth
        menuPortal
        options={PARAM_TYPE_OPTIONS}
        value={value}
        onChange={(v: string | number) => onChange(v as ParamType)}
        classes={{ trigger: styles.selectTrigger }}
      />
      {isEnumType(value) && onOpenMap && (
        <Button white size="small" onClick={onOpenMap}>
          {enumMap && enumMap.length ? `${t`Map`} (${enumMap.length})` : t`Map`}
        </Button>
      )}
    </div>
  );
};

interface IVerifyCellProps {
  verify: Verify;
  reading: string;
  units?: string;
  onTest: () => void;
}

export const VerifyCell: React.FC<IVerifyCellProps> = ({ verify, reading, units, onTest }) => {
  const readingDisplay =
    reading !== "-" && reading !== "ERR" && units ? `${reading} ${units}` : reading;
  return (
    <div className={styles.statusCluster}>
      <Button
        size="small"
        className={clsx(
          styles.testBtn,
          verify === "ok" && styles.testBtnOk,
          verify === "bad" && styles.testBtnBad,
          verify === "warn" && styles.testBtnWarn,
        )}
        onClick={onTest}
      >
        {t`Test`}
      </Button>
      <Typography
        component="span"
        variant="reading"
        color={verify === "ok" ? "success" : verify === "bad" ? "error" : undefined}
      >
        {readingDisplay}
      </Typography>
    </div>
  );
};

interface IUniversalCellProps {
  enabled?: boolean;
  point?: string;
  options: (string | Option)[];
  onToggle: () => void;
  onChangePoint: (value: string) => void;
}

export const UniversalCell: React.FC<IUniversalCellProps> = ({
  enabled,
  point,
  options,
  onToggle,
  onChangePoint,
}) => {
  return (
    <div className={styles.universalCell}>
      <Checkbox size="small" checked={!!enabled} onChange={onToggle} />
      {enabled && (
        <Select
          fullWidth
          menuPortal
          options={options as any}
          value={point || ""}
          onChange={(v: string | number) => onChangePoint(String(v))}
          classes={{ trigger: styles.selectTrigger }}
          className={styles.universalSelectMin}
        />
      )}
    </div>
  );
};

interface IErrorPointCellProps {
  enabled?: boolean;
  clearValue?: number | null;
  onToggle: () => void;
  onChangeClearValue: (value: number | null) => void;
}

/** Checkbox + conditional "Clear Value" number field (mirrors BACnet's Error point / Error Clear Value). */
export const ErrorPointCell: React.FC<IErrorPointCellProps> = ({
  enabled,
  clearValue,
  onToggle,
  onChangeClearValue,
}) => {
  return (
    <div className={styles.universalCell}>
      <Checkbox size="small" checked={!!enabled} onChange={onToggle} />
      {enabled && (
        <TextField
          size="small"
          type="number"
          fullWidth
          placeholder={t`Clear Value`}
          value={clearValue == null ? "" : String(clearValue)}
          onChange={(v: string) => onChangeClearValue(v === "" ? null : Number(v))}
          className={styles.universalSelectMin}
        />
      )}
    </div>
  );
};

export {
  REG_TYPES,
  DATA_TYPES,
  PARAM_TYPES,
  RW_OPTIONS,
  REG_TYPE_OPTIONS,
  DATA_TYPE_OPTIONS,
  PARAM_TYPE_OPTIONS,
  RW_SELECT_OPTIONS,
  RW_BOOL_OPTIONS,
  REG_TYPE_NUMERIC_OPTIONS,
  DATA_TYPE_NUMERIC_OPTIONS,
  UI_TYPE_OPTIONS,
  DATA_TYPE_CODE_OPTIONS,
  REGISTER_TYPE_COIL,
  isBitPackableRegisterType,
  BIT_NUMBER_OPTIONS,
  ENG_UNIT_OPTIONS,
  engUnitLabel,
  UNIVERSAL_POINTS,
  isEnumType,
} from "./options";
export type { Option } from "./options";
